// src/auth/UserContext.ts
import React, { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { User as FirebaseUser } from 'firebase/auth';
import { PlatformUser } from '@twinsketch/topika-model';
import UserService from 'src/services/UserService';
import { isTokenValid } from './tokenUtils';

interface UserContextType {
  firebaseUser: FirebaseUser | null;
  setFirebaseUser: React.Dispatch<React.SetStateAction<FirebaseUser | null>>;
  platformUser: PlatformUser | null;
  setPlatformUser: React.Dispatch<React.SetStateAction<PlatformUser | null>>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const UserProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [platformUser, setPlatformUser] = useState<PlatformUser | null>(null);

  useEffect(() => {
    const restoreUser = async () => {
      const hasToken = isTokenValid();
      if (hasToken && !platformUser) {
        try {
          const fetchedUser = await UserService.getUser();
          setPlatformUser(fetchedUser);
        } catch (error) {
          console.error('Failed to restore user session:', error);
          setPlatformUser(null);
        }
      }
    };

    restoreUser();
  }, [platformUser]);

  return (
    <UserContext.Provider value={{ firebaseUser, setFirebaseUser, platformUser, setPlatformUser }}>
      {children}
    </UserContext.Provider>
  );
};

// ✅ Moved outside the component
export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
